import { NavLink, Outlet } from "react-router-dom";
import { Bell, CalendarDays, Contact, House, IdCard, ScanLine, Target, User } from "lucide-react";
import { useUnread } from "@/hooks/queries";
import DesktopTopBar from "@/components/DesktopTopBar";

const TABS = [
  { to: "/", label: "Home", icon: House, end: true },
  { to: "/contacts", label: "Contacts", icon: Contact },
  { to: "/scan", label: "Scan", icon: ScanLine, center: true },
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/profile", label: "Profile", icon: User },
];
const EXTRA = [
  { to: "/dbc", label: "Digital Card", icon: IdCard },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/notifications", label: "Notifications", icon: Bell },
];

function MobileTab({ to, label, icon: Icon, end, center }) {
  if (center)
    return (
      <NavLink to={to} className="flex flex-col items-center -mt-7" data-testid="tab-scan">
        {({ isActive }) => (
          <>
            <span className="w-[58px] h-[58px] rounded-full bg-[#0000EC] flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(0,0,236,.7)] ring-4 ring-white transition-transform active:scale-95">
              <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
            </span>
            <span className={`mt-1 text-[12px] font-bold ${isActive ? "text-[#0000EC]" : "text-[#0000EC]"}`}>{label}</span>
          </>
        )}
      </NavLink>
    );
  return (
    <NavLink to={to} end={end} className="flex flex-col items-center gap-1 w-16" data-testid={`tab-${label.toLowerCase()}`}>
      {({ isActive }) => (
        <>
          <span className={`w-12 h-8 rounded-full flex items-center justify-center transition-colors ${isActive ? "bg-[#E5E5FD]" : ""}`}>
            <Icon className={`w-[22px] h-[22px] ${isActive ? "text-[#0000EC]" : "text-[#475569]"}`} strokeWidth={isActive ? 2.4 : 1.8} />
          </span>
          <span className={`text-[12px] ${isActive ? "font-bold text-[#0000EC]" : "font-medium text-[#475569]"}`}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function SideLink({ to, label, icon: Icon, end, badge }) {
  return (
    <NavLink to={to} end={end} data-testid={`side-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => `flex items-center gap-3 h-11 px-3 rounded-xl text-[15px] transition-colors ${isActive ? "bg-[#E5E5FD] text-[#0000EC] font-bold" : "text-[#475569] font-medium hover:bg-[#F1F5F9]"}`}>
      <Icon className="w-5 h-5" strokeWidth={2} />
      <span className="flex-1">{label}</span>
      {badge > 0 && <span className="min-w-5 h-5 px-1.5 rounded-full bg-[#DC2626] text-white text-[11px] font-bold flex items-center justify-center">{badge}</span>}
    </NavLink>
  );
}

export default function AppShell() {
  const unread = useUnread().data?.count ?? 0;
  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-[#E2E8F0] h-screen sticky top-0 p-5" data-testid="desktop-sidebar">
        <div className="flex items-center gap-2 px-2 mb-8">
          <img src="/logo.png" alt="TOTO" className="w-9 h-9 rounded-xl" data-testid="sidebar-logo" />
          <span className="text-[22px] font-extrabold tracking-tight">TOTO</span>
        </div>
        <nav className="flex flex-col gap-1">
          {TABS.map((t) => <SideLink key={t.to} {...t} />)}
          <div className="h-px bg-[#E2E8F0] my-3" />
          {EXTRA.map((t) => <SideLink key={t.to} {...t} badge={t.to === "/notifications" ? unread : 0} />)}
        </nav>
        <p className="mt-auto px-2 text-[12px] text-[#64748B]">Every card matters.</p>
      </aside>
      <main className="flex-1 min-w-0">
        <DesktopTopBar />
        <Outlet />
      </main>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E2E8F0] h-[72px] px-2 flex items-start justify-around pt-2" data-testid="mobile-tabbar">
        {TABS.map((t) => <MobileTab key={t.to} {...t} />)}
      </nav>
    </div>
  );
}
